// React / Next
import { useEffect, useRef, useState, useCallback } from 'react';

// Styles
import styles from './BlindsVector.module.css';

// Types
interface BlindsVectorProps {
  className?: string;
  width?: number;
  height?: number;
}

// Animation duration in ms (base duration + max delay for all paths)
const ANIMATION_DURATION = 600 + 70 * 30; // 0.6s base + ~70 columns * 0.03s delay

/**
 * BlindsVector - Animated SVG with horizontal blinds opening effect on hover
 * Paths animate with scaleX from 0 to 1 with staggered delays based on X position (column)
 */
export const BlindsVector = ({
  className = '',
  width = 496,
  height = 455,
}: BlindsVectorProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const paths = Array.from(svg.querySelectorAll('g path')) as SVGPathElement[];

    // Calculate column positions and assign delay index
    const items = paths.map((path) => {
      const box = path.getBBox();
      // Round X to group nearby elements into columns
      const roundedX = Math.round(box.x / 2) * 2;
      return { element: path, x: roundedX };
    });

    // Get unique X positions sorted from left to right
    const uniqueX = [...new Set(items.map((item) => item.x))].sort((a, b) => a - b);

    // Assign column index based on X position
    items.forEach((item) => {
      const colIndex = uniqueX.indexOf(item.x);
      item.element.style.setProperty('--col-i', String(colIndex));
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, [isAnimating]);

  return (
    <svg
      ref={svgRef}
      className={`${styles.svg} ${isAnimating ? styles.animating : ''} ${className}`}
      width={width}
      height={height}
      viewBox="0 0 496 455"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Blinds animated vector"
      role="img"
      onMouseEnter={handleMouseEnter}
    >
      <rect width="496" height="455" fill="#FFF2E2" />
      <g clipPath="url(#blindsClip)">
        <path d="M363.999 58.1118H361.258V398H363.999V58.1118Z" fill="#022E40" />
        <path d="M353.185 58.1118H350.544V398H353.185V58.1118Z" fill="#022E40" />
        <path d="M342.369 58.1118H339.829V398H342.369V58.1118Z" fill="#022E40" />
        <path d="M331.56 58.1118H329.12V398H331.56V58.1118Z" fill="#022E40" />
        <path d="M320.739 58.1118H318.406V398H320.739V58.1118Z" fill="#022E40" />
        <path d="M309.924 58.1118H307.691V398H309.924V58.1118Z" fill="#022E40" />
        <path d="M299.109 58.1118H296.978V398H299.109V58.1118Z" fill="#022E40" />
        <path d="M288.294 58.1118H286.263V398H288.294V58.1118Z" fill="#022E40" />
        <path d="M277.479 58.1118H275.549V398H277.479V58.1118Z" fill="#022E40" />
        <path d="M266.664 58.1118H264.84V398H266.664V58.1118Z" fill="#022E40" />
        <path d="M255.848 58.1118H254.125V398H255.848V58.1118Z" fill="#022E40" />
        <path d="M245.034 58.1118H243.411V398H245.034V58.1118Z" fill="#022E40" />
        <path d="M234.218 58.1118H232.696V398H234.218V58.1118Z" fill="#022E40" />
        <path d="M223.398 58.1118H221.982V398H223.398V58.1118Z" fill="#022E40" />
        <path d="M212.582 58.1118H211.268V398H212.582V58.1118Z" fill="#022E40" />
        <path d="M201.773 58.1118H200.559V398H201.773V58.1118Z" fill="#022E40" />
        <path d="M190.958 58.1118H189.845V398H190.958V58.1118Z" fill="#022E40" />
        <path d="M180.137 58.1118H179.13V398H180.137V58.1118Z" fill="#022E40" />
        <path d="M169.322 58.1118H168.416V398H169.322V58.1118Z" fill="#022E40" />
        <path d="M158.507 58.1118H157.701V398H158.507V58.1118Z" fill="#022E40" />
        <path d="M147.692 58.1118H146.987V398H147.692V58.1118Z" fill="#022E40" />
        <path d="M136.877 58.1118H136.278V398H136.877V58.1118Z" fill="#022E40" />
        <path d="M138.97 299.177H134.187V361.707H138.97V299.177Z" fill="#15B1CD" />
        <path d="M149.663 299.177H145.024V361.707H149.663V299.177Z" fill="#15B1CD" />
        <path d="M160.348 299.177H155.855V361.707H160.348V299.177Z" fill="#15B1CD" />
        <path d="M171.046 299.177H166.693V361.707H171.046V299.177Z" fill="#15B1CD" />
        <path d="M181.739 299.177H177.531V361.707H181.739V299.177Z" fill="#15B1CD" />
        <path d="M192.43 299.177H188.368V361.707H192.43V299.177Z" fill="#15B1CD" />
        <path d="M203.122 299.177H199.2V361.707H203.122V299.177Z" fill="#15B1CD" />
        <path d="M213.815 299.177H210.038V361.707H213.815V299.177Z" fill="#15B1CD" />
        <path d="M224.506 299.177H220.875V361.707H224.506V299.177Z" fill="#15B1CD" />
        <path d="M235.198 299.177H231.707V361.707H235.198V299.177Z" fill="#15B1CD" />
        <path d="M213.815 299.177H210.038V332.429H213.815V299.177Z" fill="#6AEAFD" />
        <path d="M224.506 299.177H220.875V332.429H224.506V299.177Z" fill="#6AEAFD" />
        <path d="M235.198 299.177H231.707V332.429H235.198V299.177Z" fill="#6AEAFD" />
        <path d="M322.614 58.1118H316.169V332.569H322.614V58.1118Z" fill="#6AEAFD" />
        <path d="M311.716 58.1118H305.595V332.569H311.716V58.1118Z" fill="#6AEAFD" />
        <path d="M300.805 58.1118H295.015V332.569H300.805V58.1118Z" fill="#6AEAFD" />
        <path d="M289.906 58.1118H284.439V332.569H289.906V58.1118Z" fill="#6AEAFD" />
        <path d="M279.001 58.1118H273.865V332.569H279.001V58.1118Z" fill="#6AEAFD" />
        <path d="M268.097 58.1118H263.291V332.569H268.097V58.1118Z" fill="#6AEAFD" />
        <path d="M257.197 58.1118H252.716V332.569H257.197V58.1118Z" fill="#6AEAFD" />
        <path d="M246.293 58.1118H242.142V332.569H246.293V58.1118Z" fill="#6AEAFD" />
        <path d="M268.097 58.1118H263.291V300.485H268.097V58.1118Z" fill="#022E40" />
        <path d="M257.197 58.1118H252.716V300.485H257.197V58.1118Z" fill="#022E40" />
        <path d="M246.293 58.1118H242.142V300.485H246.293V58.1118Z" fill="#022E40" />
        <path d="M140.9 58.2404H133V300.977H140.9V58.2404Z" fill="#022E40" />
        <path d="M151.492 58.2404H143.854V300.977H151.492V58.2404Z" fill="#022E40" />
        <path d="M162.078 58.2404H154.709V300.977H162.078V58.2404Z" fill="#022E40" />
        <path d="M172.668 58.2404H165.562V300.977H172.668V58.2404Z" fill="#022E40" />
        <path d="M183.248 58.2404H176.411V300.977H183.248V58.2404Z" fill="#022E40" />
        <path d="M193.834 58.2404H187.266V300.977H193.834V58.2404Z" fill="#022E40" />
        <path d="M204.426 58.2404H198.12V300.977H204.426V58.2404Z" fill="#022E40" />
        <path d="M215.012 58.2404H208.975V300.977H215.012V58.2404Z" fill="#022E40" />
        <path d="M225.602 58.2404H219.828V300.977H225.602V58.2404Z" fill="#022E40" />
        <path d="M236.188 58.2404H230.683V300.977H236.188V58.2404Z" fill="#022E40" />
        <path d="M281.424 58H271.526V269.446H281.424V58Z" fill="#022E40" />
        <path d="M270.329 58H261.069V269.446H270.329V58Z" fill="#022E40" />
        <path d="M259.24 58H250.612V269.446H259.24V58Z" fill="#022E40" />
        <path d="M248.15 58H240.155V269.446H248.15V58Z" fill="#022E40" />
        <path d="M137.286 58.2404H136.296V268.127H137.286V58.2404Z" fill="#022E40" />
        <path d="M148.376 58.218H146.675V268.278H148.376V58.218Z" fill="#022E40" />
        <path d="M159.454 58.1901H157.048V268.424H159.454V58.1901Z" fill="#022E40" />
        <path d="M170.543 58.1621H167.427V268.569H170.543V58.1621Z" fill="#022E40" />
        <path d="M181.626 58.1342H177.805V268.714H181.626V58.1342Z" fill="#022E40" />
        <path d="M192.716 58.1062H188.184V268.859H192.716V58.1062Z" fill="#022E40" />
        <path d="M203.805 58.0782H198.562V269.005H203.805V58.0782Z" fill="#022E40" />
        <path d="M214.889 58.0559H208.941V269.156H214.889V58.0559Z" fill="#022E40" />
        <path d="M225.978 58.028H219.32V269.301H225.978V58.028Z" fill="#022E40" />
        <path d="M237.056 58H229.693V269.446H237.056V58Z" fill="#022E40" />
      </g>
      <defs>
        <clipPath id="blindsClip">
          <rect width="231" height="340" fill="white" transform="translate(133 58)" />
        </clipPath>
      </defs>
    </svg>
  );
};

